import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

// The Search Console property list has one implementation in the app, on the sibling tool that
// introduced it; both tools pick a property out of everything Search Console exposes to the user,
// which is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import Overview from './components/Overview';
import PageChanges from './components/PageChanges';
import { fetchPageChunk, fetchPeriod } from './query';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { buildReportCsv } from './exportRows';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    comparability,
    coverageFor,
    hasDuplicateKeys,
    joinRows,
    metricsFor,
    normalizeTotals,
    reportsPosition,
} from '../GscTools/metrics';
import { COMPARISON, defaultPeriod, detailPeriod, periodEndingOn } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import {
    hasScope,
    readParams,
    scopeDraftFromParams,
    scopeParamsFromScope,
    writeParams,
} from '../GscTools/url';

const IDLE_PERIOD = { status: 'idle', totals: null, series: [], coverage: null, error: null };

const IDLE_PAGES = {
    status: 'idle',
    rows: [],
    currentChunk: null,
    priorChunk: null,
    filter: 'all',
    error: null,
    loadingMore: false,
    period: null,
    shortened: false,
};

/**
 * Search Overview — one Search Console property's performance across two periods, on whichever
 * search surface the scope names (Web Search, Discover, Google News, the News, Image or Video
 * tabs).
 *
 * The report is assembled from live Search Console responses rather than from anything GDDash
 * stores, and it only ever asks for the scope the user applied. Each period is fetched by its own
 * request so a baseline that fails leaves the current period's panels standing, and every
 * response is tagged with the generation that asked for it: a slow answer to a scope the user has
 * since moved on from is dropped rather than painted over the newer one.
 *
 * Nothing fetched here is persisted. The data lives in this component's state for as long as the
 * page is open, and the exports in block 06 build their files from that same memory. The scope is
 * the one exception: it is kept in the page's address, so a reload or a shared link opens the same
 * report rather than an empty scope bar, and a report found in the URL is run on arrival.
 */
class SearchOverview extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleSelectMetric = this.handleSelectMetric.bind(this);
        this.handleLoadMorePages = this.handleLoadMorePages.bind(this);
        this.handleFilterPages = this.handleFilterPages.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleDownloadReportCsv = this.handleDownloadReportCsv.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();

        // Incremented by every apply and refresh; responses carrying an older value are stale.
        this.generation = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the scope it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = scopeDraftFromParams(params);
        this.urlHasScope = hasScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            // The property's most recent day with final data, once probed for.
            latestDate: '',

            draft: { ...emptyDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            metric: 'clicks',
            capturing: false,
            // The scope opens for the first run and folds away once a report is on screen.
            controlsOpen: true,

            current: { ...IDLE_PERIOD },
            baseline: { ...IDLE_PERIOD },

            pages: { ...IDLE_PAGES },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     *
     * This is a suggestion, not a watermark: it moves the draft dates only, costs one small
     * request, and is abandoned the moment the user edits them.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                // The user may have picked another property, or set their own dates, while the
                // probe was in flight; either way the suggestion is no longer wanted.
                if (state.draft.siteUrl !== siteUrl) return null;

                // Kept as well as applied: the scope controls count their date presets back from
                // this day, so one click lands on days the property actually has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveScope(this.state.draft, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleSelectMetric(metric) {
        this.setState({ metric });
    }

    handleFilterPages(filter) {
        this.setState((state) => ({ pages: { ...state.pages, filter } }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Runs the applied scope: one request per period, both in flight at once, each landing on its
     * own, and the first chunk of page rows alongside them. Page changes are reset rather than
     * kept — they belong to the scope that fetched them, and leaving them on screen under new
     * dates would be showing one period's pages beside another's totals.
     *
     * The page dimension used to be asked for by hand, on the grounds that it costs a request per
     * period that most visits never needed. Applying a scope is that ask: the report is read as
     * one page, so its last block is fetched with the rest rather than behind a second click.
     */
    load(scope) {
        this.generation += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(scopeParamsFromScope(scope));

        this.setState(
            (state) => ({
                applied: scope,
                controlsOpen: false,
                loading: true,
                metric: metricsFor(scope.searchType).includes(state.metric)
                    ? state.metric
                    : 'clicks',
                fetchedAt: new Date().toISOString(),
                current: { ...IDLE_PERIOD, status: 'loading' },
                baseline: scope.baseline
                    ? { ...IDLE_PERIOD, status: 'loading' }
                    : { ...IDLE_PERIOD },
                pages: { ...IDLE_PAGES },
            }),
            // loadPages reads the applied scope off state, so it runs once this has landed. It
            // carries its own loading state and is not waited on with the periods: a page request
            // that fails leaves the totals above it standing.
            () => this.loadPages(),
        );

        const requests = [this.loadPeriod(scope, scope.current, 'current', generation)];
        if (scope.baseline) {
            requests.push(this.loadPeriod(scope, scope.baseline, 'baseline', generation));
        }

        Promise.all(requests).then(() => {
            if (this.isMounted_ && generation === this.generation)
                this.setState({ loading: false });
        });
    }

    async loadPeriod(scope, period, key, generation) {
        try {
            const { totals, daily } = await fetchPeriod({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: scope.siteUrl,
                period,
                searchType: scope.searchType,
                country: scope.country,
                device: scope.device,
                dataState: scope.dataState,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const hasPosition = reportsPosition(scope.searchType);

            this.setState({
                [key]: {
                    status: 'done',
                    totals: normalizeTotals(totals, hasPosition),
                    series: buildSeries(daily, period, hasPosition),
                    coverage: coverageFor(daily, period),
                    error: null,
                },
            });
        } catch (err) {
            console.log(key, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                [key]: {
                    ...IDLE_PERIOD,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /**
     * Fetches one chunk of page rows for each period and joins them.
     *
     * The two periods keep independent cursors: Search Console can hand back a full chunk for one
     * window and a short one for the other, and advancing them together would either re-read rows
     * or skip them. Duplicate URLs inside a period stop that period rather than being counted
     * twice.
     */
    async loadPages({ more = false } = {}) {
        const scope = this.state.applied;
        if (!scope || scope.comparison === COMPARISON.NONE || !scope.baseline) return;

        const generation = this.generation;
        const { period, shortened } = detailPeriod(scope.current);
        const baselineDetail = detailPeriod(scope.baseline);

        this.setState((state) => ({
            pages: {
                ...state.pages,
                status: more ? state.pages.status : 'loading',
                loadingMore: more,
                period,
                shortened: shortened || baselineDetail.shortened,
                error: null,
            },
        }));

        const previousCurrent = more ? this.state.pages.currentChunk : null;
        const previousPrior = more ? this.state.pages.priorChunk : null;

        const request = (detailPeriod, chunk) => {
            if (more && (!chunk || chunk.nextOffset === null)) return Promise.resolve(chunk);
            return fetchPageChunk({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: scope.siteUrl,
                period: detailPeriod,
                searchType: scope.searchType,
                country: scope.country,
                device: scope.device,
                dataState: scope.dataState,
                offset: more ? chunk.nextOffset : 0,
            }).then((result) => {
                const rows = [...(chunk?.rows ?? []), ...result.rows];
                if (hasDuplicateKeys(rows)) {
                    throw new Error(this.props.t('tools.gsc.duplicate_rows'));
                }
                return { ...result, rows };
            });
        };

        try {
            const [currentChunk, priorChunk] = await Promise.all([
                request(period, previousCurrent),
                request(baselineDetail.period, previousPrior),
            ]);

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState((state) => ({
                pages: {
                    ...state.pages,
                    status: 'done',
                    loadingMore: false,
                    currentChunk,
                    priorChunk,
                    rows: joinRows(
                        currentChunk?.rows,
                        priorChunk?.rows,
                        reportsPosition(scope.searchType),
                    ),
                },
            }));
        } catch (err) {
            console.log('pages', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState((state) => ({
                pages: {
                    ...state.pages,
                    // A failure while loading more keeps the rows already joined; a failure on the
                    // first chunk has nothing to keep.
                    status: state.pages.rows.length ? 'done' : 'error',
                    loadingMore: false,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            }));
        }
    }

    handleLoadMorePages() {
        this.loadPages({ more: true });
    }

    handlePrint() {
        window.print();
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /** A filename that says which property and window the file holds. */
    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}_${scope?.siteUrl ?? 'property'}_${scope?.current?.startDate ?? ''}`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    exportContext() {
        return {
            t: this.props.t,
            scope: this.state.applied,
            current: this.state.current,
            baseline: this.state.baseline,
            fetchedAt: this.state.fetchedAt
                ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                : null,
        };
    }

    handleDownloadReportCsv() {
        if (!this.state.applied) return;
        downloadCsv(
            buildReportCsv(this.exportContext()),
            `${this.exportName('search-overview')}.csv`,
        );
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls that produced the report and the export actions themselves are
     * left out of the frame, while the scope and coverage labels stay in it, so the picture still
     * says what it is a picture of.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-exports') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `search-overview_${
                this.state.applied?.current?.startDate ?? 'report'
            }.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /** The weaker of the two chunks decides whether more rows can still be asked for. */
    pagesStopReason() {
        const { currentChunk, priorChunk } = this.state.pages;
        const reasons = [currentChunk?.stopReason, priorChunk?.stopReason].filter(Boolean);

        if (!reasons.length) return 'empty_page';
        if (reasons.includes('more_available')) return 'more_available';
        if (reasons.includes('product_cap')) return 'product_cap';
        return 'empty_page';
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const comparison = comparability(
            this.state.current.coverage,
            applied?.baseline ? this.state.baseline.coverage : null,
        );

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.search_overview.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.search_overview.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={this.state.current.status !== 'done' || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={this.state.current.status !== 'done'}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                />

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-area-chart" />
                            </span>
                            <h4>{t('tools.search_overview.idle_title')}</h4>
                            <p>{t('tools.search_overview.idle')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <CoverageStrip
                            scope={applied}
                            current={this.state.current}
                            baseline={applied.baseline ? this.state.baseline : null}
                            fetchedAt={this.state.fetchedAt}
                        />

                        <Overview
                            scope={applied}
                            current={this.state.current}
                            baseline={this.state.baseline}
                            comparison={comparison}
                            metric={this.state.metric}
                            onSelectMetric={this.handleSelectMetric}
                            onRefresh={this.handleRefresh}
                            onDownloadCsv={this.handleDownloadReportCsv}
                        />

                        <PageChanges
                            scope={applied}
                            status={this.state.pages.status}
                            error={this.state.pages.error}
                            rows={this.state.pages.rows}
                            period={this.state.pages.period}
                            shortened={this.state.pages.shortened}
                            stopReason={this.pagesStopReason()}
                            filter={this.state.pages.filter}
                            loadingMore={this.state.pages.loadingMore}
                            onLoadMore={this.handleLoadMorePages}
                            onFilter={this.handleFilterPages}
                        />
                    </>
                )}
            </div>
        );
    }
}

export default withTranslation()(SearchOverview);
